import { TransactionList } from '@back-office/http';
import {
  getDatetimeText,
  MenuBar,
  QrTypeMenu,
  removeEmpty,
  TransactionStatus,
  TransactionSubStatus,
} from '@back-office/ui';
import { DateRange } from '@material-ui/pickers';
import endOfDay from 'date-fns/endOfDay';
import { useLocalStore } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import qs from 'query-string';
import { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMount, useToggle, useUnmount } from 'react-use';
import { Store } from '.';
import { statusBar } from '../../constants';
import useInject from '../../hooks/useInject';
import TransactionModel from '../../stores/transactions.store';

type UseViewModelReturn = {
  state: Store;
  getDropdownPlaceholder: (txStatus?: Store['status']) => string;
  transaction: Instance<typeof TransactionModel>;
  on: boolean;
  toggle: (value: boolean) => void;
};

const useViewModel = (): UseViewModelReturn => {
  const { t } = useTranslation();
  const { getTransactionList, transaction, auth } = useInject((root) => root);
  const [on, toggle] = useToggle(false);

  const state = useLocalStore<Store>(() => ({
    date: [null, null],
    openCalendar: false,
    openSearchBar: false,
    isMouseOver: false,
    keyword: '',
    anchorEl: null,
    anchorElQrType: null,
    status: '',
    subStatus: '',
    combineStatus: '',
    qrType: '',
    qrTypeLabe: 'transaction_list.qr_types.all',
    start: 0,
    page: 1,
    txId: null,
    isDetail: false,
    get query(): string {
      return qs.stringify(
        removeEmpty({
          limit: 20,
          merchantID: auth.currentUser.merchantID,
          fromDate: this.date[0] ? getDatetimeText(this.date[0], 'json') : null,
          toDate: this.date[1]
            ? getDatetimeText(endOfDay(this.date[1]), 'json')
            : null,
          key: this.keyword || null,
          status: this.status || null,
          subStatus: this.subStatus || null,
          start: this.start || null,
          qrType: this.qrType || null,
        })
      );
    },
    handleOnGo() {
      this.page = 1;
      this.start = 0;
      getTransactionList(this.query);
    },
    handleOnChangeSearchbar(event: ChangeEvent<HTMLInputElement>) {
      this.keyword = event.target.value;
    },
    handleOnClickSearchbar() {
      this.openSearchBar = true;
    },
    handleOnClickAway() {
      this.openSearchBar = !!this.keyword;
    },
    handleOnChangeDatePicker(date: DateRange<Date>) {
      this.date = date;
    },
    handleMouseEvent(isEnter: boolean) {
      this.isMouseOver = isEnter;
    },
    handleOnClear(event: MouseEvent<HTMLOrSVGElement>) {
      event.stopPropagation();
      this.date = [null, null];
      this.handleOnGo();
    },
    handleOnClickDatePicker() {
      this.openCalendar = true;
    },
    handleOnCloseDatePicker() {
      this.openCalendar = false;
      this.handleOnGo();
    },
    handleOnClickMenubar(event: MouseEvent<HTMLButtonElement>) {
      this.anchorEl = event.currentTarget;
    },
    handleOnClickMenuItem(item: MenuBar | QrTypeMenu) {
      this.anchorEl = null;
      this.combineStatus = item.status as TransactionStatus;
      const query = mapStatusToQuery(item.status as string);
      this.status = query.status as TransactionStatus;
      this.subStatus = query.subStatus as TransactionSubStatus;
      this.handleOnGo();
    },
    handleOnClose() {
      this.anchorEl = null;
    },
    handleOnClickQrType(event: MouseEvent<HTMLButtonElement>) {
      this.anchorElQrType = event.currentTarget;
    },
    handleOnClickQrTypeItem(item: QrTypeMenu) {
      this.anchorElQrType = null;
      this.qrType = item.status;
      this.qrTypeLabe = item.wording;
      this.handleOnGo();
    },
    handleOnCloseQrType() {
      this.anchorElQrType = null;
    },
    handleOnChangePage(newPage: number) {
      this.page = newPage;
      this.start = (newPage - 1) * 20;
      getTransactionList(this.query);
    },
    handleOnClickRow(tx: TransactionList) {
      this.txId = tx.id;
      this.isDetail = true;
    },
    handleOnKeyDown(event: KeyboardEvent<HTMLDivElement>) {
      if (event.key === 'Enter') {
        getTransactionList(this.query);
      }
    },
    handleOnClickBreadcrumbs() {
      this.txId = null;
      this.isDetail = false;
    },
  }));

  useMount(() => {
    getTransactionList(state.query);
  });

  useUnmount(() => {
    transaction.reset();
  });

  const getDropdownPlaceholder = (txStatus?: Store['status']): string => {
    const translationPath = statusBar.find(
      (statusItem) => statusItem.status === txStatus
    )?.wording;
    if (translationPath) {
      return t(translationPath);
    }
    return t('transaction_list.status.all_status');
  };

  const mapStatusToQuery = (
    status: string
  ): { status: string; subStatus?: string } => {
    switch (status) {
      case TransactionStatus.AuthorizedVoided:
        return { status: 'authorized', subStatus: 'void' };
      case TransactionStatus.ClearedPendingRefund:
        return { status: 'cleared', subStatus: 'pending' };
      case TransactionStatus.ClearedRefunded:
        return { status: 'cleared', subStatus: 'cleared' };
      case TransactionStatus.RefundRequestPending:
        return { status: 'cleared', subStatus: 'refund_request_pending' };
    }
    return { status };
  };

  return { state, getDropdownPlaceholder, on, toggle, transaction };
};

export default useViewModel;
